import { Logo } from "@screener/uikit/components/logo";
import { cn } from "@screener/uikit/lib/utils";
import type { ReactNode, Ref } from "react";
import { DecorativeDensityCards } from "./decorative-density-cards";

/** Описание сервиса под карточками правой колонки (handoff «Auth.dc.html»). */
const SERVICE_TITLE = "Скринер плотностей";
const SERVICE_DESCRIPTION =
  "Держит стаканы и ленты сделок криптобирж, находит крупные лимитные заявки и ведёт каждую от обнаружения до исхода. Отличает заявку, которая держит цену, от спуфинга и котировки маркетмейкера.";

interface AuthLayoutProps {
  /** Содержимое левой колонки: форма или сообщение; 300 px по центру. */
  children: ReactNode;
  /** Страница занята ожиданием ответа (`aria-busy`). */
  busy?: boolean;
}

/**
 * Две колонки экранов входа, регистрации и недействительной ссылки
 * (stage9-auth-redesign, Д4): слева лого и форма, справа декоративные
 * карточки плотностей и описание сервиса. Ниже брейкпоинта `md` (768 px)
 * правой колонки нет. Экранный компонент, а не UIKit: карточки справа
 * повторяют вид карточки плотности.
 */
export function AuthLayout({ children, busy = false }: AuthLayoutProps) {
  return (
    <main
      data-slot="auth-layout"
      aria-busy={busy || undefined}
      className="grid min-h-dvh bg-background text-foreground md:h-dvh md:grid-cols-2"
    >
      <div className="flex min-h-dvh flex-col px-7 py-6 md:min-h-0">
        <div className="flex items-center gap-2">
          <Logo animated aria-hidden="true" className="h-auto w-5" />
          <span className="text-base font-semibold tracking-[-0.005em]">kelv</span>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-[300px] max-w-full">{children}</div>
        </div>
      </div>
      <aside
        data-slot="auth-layout-aside"
        className="relative hidden min-h-0 flex-col gap-7 overflow-hidden border-l border-border bg-background px-10 pt-6 pb-10 md:flex"
      >
        <DecorativeDensityCards className="flex-1" />
        <div className="relative flex flex-col items-start gap-5">
          <Logo animated aria-hidden="true" className="h-auto w-10" />
          <div className="flex max-w-[340px] flex-col items-start gap-2">
            <span className="text-base leading-[1.3] font-semibold">{SERVICE_TITLE}</span>
            <p className="text-[13px] leading-normal text-pretty text-muted-foreground">
              {SERVICE_DESCRIPTION}
            </p>
          </div>
        </div>
      </aside>
    </main>
  );
}

/**
 * Заголовок экрана: `h1` по центру и необязательная строка под ним
 * (подзаголовок, ссылка на вход).
 */
export function AuthHeading({
  title,
  className,
  headingRef,
  children,
}: {
  title: string;
  className?: string;
  /**
   * Ссылка на `h1`. Передана — заголовок принимает фокус программно
   * (`tabIndex={-1}`): экран, сменивший форму, переводит на него фокус.
   */
  headingRef?: Ref<HTMLHeadingElement>;
  children?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col items-center gap-2 text-center", className)}>
      <h1
        ref={headingRef}
        tabIndex={headingRef ? -1 : undefined}
        className="text-xl leading-[1.2] font-semibold outline-none"
      >
        {title}
      </h1>
      {children}
    </div>
  );
}

/**
 * Текст ожидания в левой колонке («Проверяем ссылку…», «Проверяем
 * регистрацию…»): раскладка остаётся той же, что у формы, и не прыгает.
 */
export function AuthStatus({ children }: { children: ReactNode }) {
  return (
    <p role="status" aria-busy="true" className="text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

/** Ссылка «Уже есть учётная запись? Войти» — на экран входа. */
export function SignInHint({ className }: { className?: string }) {
  return (
    <p className={cn("text-xs leading-[1.4] text-muted-foreground", className)}>
      Уже есть учётная запись?{" "}
      <a
        href="#/"
        className="text-foreground underline underline-offset-[3px] hover:text-muted-foreground"
      >
        Войти
      </a>
    </p>
  );
}
